import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Loader2, Zap, ShieldAlert } from 'lucide-react';
import { normalizeAuPhone, formatAgent, StatusBadge, ScoreBadge } from '../domainUtils';
import CallRecordingButton from '../../../../components/CallRecordingButton';
import WonPill from '../../../../components/WonPill';

const SPECIAL_UPPER = ['IT', 'MSP', 'BDM', 'B2B', 'HR', 'CRM'];
const formatCallType = (raw) =>
  (raw || '').split('_').map(w => {
    const u = w.toUpperCase();
    return SPECIAL_UPPER.includes(u) ? u : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join(' ');

const VoiceFabricChip = ({ callId, count }) => {
  if (!count) return null;
  return (
    <Link
      to={`/calls/${callId}/voicefabric`}
      className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 transition-colors"
      title={`${count} VoiceFabric action${count === 1 ? '' : 's'} fired on this call — click to view trace`}
      data-testid={`vf-chip-${callId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Zap className="w-2.5 h-2.5" /> {count}
    </Link>
  );
};

const GovernanceBreachChip = ({ callId, reason }) => {
  const label = reason === 'governance_and_score'
    ? 'Governance + Score'
    : 'Governance';
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-600/20 text-red-300 border border-red-500/40"
      title="Compliance / trust breach detected — escalated independently of the numeric score"
      data-testid={`governance-chip-${callId}`}
    >
      <ShieldAlert className="w-2.5 h-2.5" /> {label}
    </span>
  );
};

const CallRow = ({ c, score, vfCount }) => {
  const fromNum = normalizeAuPhone(c.call_direction === 'inbound' ? c.customer_phone : c.agent_id);
  const toNum   = normalizeAuPhone(c.call_direction === 'inbound' ? c.agent_id : c.customer_phone);
  const callDate = new Date(c.call_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' });
  const callTime = new Date(c.call_date).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });

  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
      <td className="px-4 py-3 text-white/80 font-medium whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span>{formatAgent(c.agent_id, c.agent_name, c.extension)}</span>
          <VoiceFabricChip callId={c.call_id} count={vfCount} />
          {c.governance_breach && (
            <GovernanceBreachChip callId={c.call_id} reason={c.escalation_reason} />
          )}
          {c.won && (
            <WonPill source={c.chase_outcome_source} by={c.chase_outcome_by} />
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-white/40 font-mono text-xs">{fromNum}</td>
      <td className="px-4 py-3 text-white/40 font-mono text-xs">{toNum}</td>
      <td className="px-4 py-3 text-white/40 text-xs whitespace-nowrap">{callDate}</td>
      <td className="px-4 py-3 text-white/40 text-xs whitespace-nowrap">{callTime}</td>
      <td className="px-4 py-3 text-white/40 whitespace-nowrap">{formatCallType(c.call_type)}</td>
      <td className="px-4 py-3 text-white/40">{Math.floor(c.duration_seconds / 60)}m {c.duration_seconds % 60}s</td>
      <td className="px-4 py-3">
        {score != null ? <ScoreBadge score={score} /> : <span className="text-white/20 text-xs">—</span>}
      </td>
      <td className="px-4 py-3"><StatusBadge status={c.status} gradingError={c.grading_error} /></td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          <CallRecordingButton callId={c.call_id} size="xs" />
          <Link to={`/calls/${c.call_id}`} className="text-teal-400 hover:text-teal-300 transition-colors" data-testid={`view-call-${c.call_id}`}>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </td>
    </tr>
  );
};

const SortBtn = ({ field, label, sortField, sortDir, onSort }) => (
  <th
    className="text-left text-white/30 text-xs font-medium px-4 py-2.5 whitespace-nowrap select-none cursor-pointer hover:text-white/50 transition-colors"
    onClick={() => onSort(field)}
  >
    {label}
    <span className="ml-1">
      {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : <span className="text-white/15">↕</span>}
    </span>
  </th>
);

const StaticTh = ({ label }) => (
  <th className="text-left text-white/30 text-xs font-medium px-4 py-2.5 whitespace-nowrap">{label}</th>
);

export const CallsTable = ({ loading, calls, gradings, vfCounts = {}, sortField, sortDir, onSort }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-7 h-7 text-white/20 animate-spin" />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/5">
            <SortBtn field="agent_name" label="Agent" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <StaticTh label="From" />
            <StaticTh label="To" />
            <SortBtn field="call_date" label="Date" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <StaticTh label="Time" />
            <SortBtn field="call_type" label="Type" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortBtn field="duration_seconds" label="Duration" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortBtn field="score" label="Score" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <SortBtn field="status" label="Status" sortField={sortField} sortDir={sortDir} onSort={onSort} />
            <StaticTh label="" />
          </tr>
        </thead>
        <tbody>
          {calls.length === 0 ? (
            <tr>
              <td colSpan={10} className="px-4 py-10 text-center text-white/20 text-sm">
                No calls match your search
              </td>
            </tr>
          ) : calls.map(c => <CallRow key={c.call_id} c={c} score={gradings[c.call_id]} vfCount={vfCounts[c.call_id]} />)}
        </tbody>
      </table>
    </div>
  );
};
